import { useState, useCallback, useRef, useEffect } from "react";
import type { SttBackendName } from "../services/stt";
import { getSttBackend, getResolvedBackendName, isSttSupported } from "../services/stt";
import { useVoiceLanguage } from "./useVoiceLanguage";

interface UseDictationOptions {
  /** Auto-stop after this many ms of silence. Default 30000 (long; suits a "press-mic-to-dictate" flow). */
  silenceTimeout?: number;
  /** Fired with the accumulated final transcript when the session ends with content. */
  onTranscript?: (text: string, meta: { viaTimeout: boolean }) => void;
  onError?: (err: Error) => void;
}

interface UseDictationReturn {
  isSupported: boolean;
  backendName: SttBackendName | null;
  startListening: () => Promise<void>;
  stopListening: () => Promise<void>;
  isListening: boolean;
  transcript: string;
  interimTranscript: string;
  resetTranscript: () => void;
  /** Timestamp (Date.now()) of the last interim or final speech result.
   *  Null while not listening or before any speech has been detected.
   *  Consumers can pair this with the configured silenceTimeout to render a
   *  countdown UI (the silence timer in the backend resets on every result). */
  lastActivityAt: number | null;
  error: Error | null;
}

export function useDictation(options: UseDictationOptions = {}): UseDictationReturn {
  const { silenceTimeout = 30000, onTranscript, onError } = options;
  const language = useVoiceLanguage();

  const [isSupported, setIsSupported] = useState(false);
  const [backendName, setBackendName] = useState<SttBackendName | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [lastActivityAt, setLastActivityAt] = useState<number | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const isMountedRef = useRef(true);
  const accumulatedRef = useRef("");

  // Keep callbacks + timeout in a ref so changing them doesn't re-bind startListening.
  const optsRef = useRef({ onTranscript, onError, silenceTimeout });
  optsRef.current = { onTranscript, onError, silenceTimeout };

  useEffect(() => {
    isMountedRef.current = true;
    isSttSupported()
      .then((supported) => {
        if (!isMountedRef.current) return;
        setIsSupported(supported);
        setBackendName(getResolvedBackendName());
      })
      .catch(() => {
        if (isMountedRef.current) setIsSupported(false);
      });
    return () => {
      isMountedRef.current = false;
      // Best-effort stop on unmount; backend tracks its own active recognition.
      getSttBackend()
        .then((b) => b.stopRecognition())
        .catch(() => undefined);
    };
  }, []);

  const startListening = useCallback(async (): Promise<void> => {
    if (isListening) return;
    setError(null);
    accumulatedRef.current = "";
    setTranscript("");
    setInterimTranscript("");
    setLastActivityAt(null);

    let backend;
    try {
      backend = await getSttBackend();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      if (isMountedRef.current) setError(error);
      optsRef.current.onError?.(error);
      throw error;
    }

    if (!isMountedRef.current) return;

    try {
      await backend.startRecognition({
        language,
        silenceTimeout: optsRef.current.silenceTimeout,
        onStart: () => {
          if (isMountedRef.current) setIsListening(true);
        },
        onInterimResult: (text) => {
          if (!isMountedRef.current) return;
          setInterimTranscript(text);
          setLastActivityAt(Date.now());
        },
        onFinalResult: (text) => {
          if (!isMountedRef.current) return;
          if (!text.trim()) return;
          const sep = accumulatedRef.current ? " " : "";
          accumulatedRef.current += sep + text;
          setTranscript(accumulatedRef.current);
          setInterimTranscript("");
          setLastActivityAt(Date.now());
        },
        onEnd: ({ viaTimeout }) => {
          if (!isMountedRef.current) return;
          setIsListening(false);
          setInterimTranscript("");
          setLastActivityAt(null);
          const final = accumulatedRef.current.trim();
          if (final) optsRef.current.onTranscript?.(final, { viaTimeout });
        },
        onError: (err) => {
          if (!isMountedRef.current) return;
          setError(err);
          setIsListening(false);
          optsRef.current.onError?.(err);
        },
      });
    } catch (err) {
      if (isMountedRef.current) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setIsListening(false);
      }
      throw err;
    }
  }, [language, isListening]);

  const stopListening = useCallback(async (): Promise<void> => {
    const backend = await getSttBackend().catch(() => null);
    if (!backend) return;
    await backend.stopRecognition();
    // onEnd handler will reset state and invoke onTranscript.
  }, []);

  const resetTranscript = useCallback(() => {
    setTranscript("");
    setInterimTranscript("");
    accumulatedRef.current = "";
  }, []);

  return {
    isSupported,
    backendName,
    startListening,
    stopListening,
    isListening,
    transcript,
    interimTranscript,
    resetTranscript,
    lastActivityAt,
    error,
  };
}
